import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { NextSeo } from "next-seo";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { Routes } from "~/routes";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    FilterOperators,
    QueryResponse,
    SortDirections,
    Filter,
    ApiQuery,
    FilterMetadata,
} from "~/modules/filters/types";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import {
    LeagueTableGroupTypes,
    LeagueTableTab,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import {
    ASSET_CLASS_IDS,
    MEASURE_IDS,
    SOCIAL_OG_IMAGE_URL,
    TOKENIZATION_TYPE_IDS,
    DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
    DISTRIBUTION_TYPE_TOOLTIPS,
    MEASURE_SLUGS,
} from "~/consts";
import { AssetClassIndividualPage } from "~/modules/asset_classes/pages/individual";
import {
    TokenTimeseriesGroupBy,
    TokenTimeseriesColumns,
} from "~/modules/token_timeseries/types";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import {
    NetFlowsData,
    queryNetFlows,
} from "~/modules/fund_flows/api/queryNetFlows";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { queryTokenAggregateValueWith30D } from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import { AggregateValueTypes } from "~/components/AggregateDisplay/types";
import { queryAssetFilterMeta, queryAssets } from "~/modules/assets/api/router";
import { ColumnMetadata } from "~/components/DataGrid";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { NET_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/NetAssetValue";
import { NETWORKS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Networks";
import { ASSET_CLASS_COLUMN_NO_LINK } from "~/modules/assets/components/AssetTable/columns/AssetClass";
import { ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import { getTimeseriesModes } from "~/modules/visualization/components/Timeseries/getTimeseriesModes";
import { SEVEN_DAYS_AGO, THIRTY_DAYS_AGO } from "~/utils/dates/consts";
import { getTopTransfers } from "~/modules/transactions/api/getTopTransfers";
import { TopTransfersData } from "~/modules/transactions/components/TopTransfersCard/types";
import { TransactionColumns } from "~/modules/transactions/types";
import { BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/BridgedTokenValueDollar";
import { CONTRACT_ADDRESS_COLUMN } from "~/modules/assets/components/AssetTable/columns/ContractAddress";
import { MANAGEMENT_FEE_COLUMN } from "~/modules/assets/components/AssetTable/columns/ManagementFee";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import { MANAGER_COLUMN } from "~/modules/assets/components/AssetTable/columns/Manager";
import {
    PERFORMANCE_1_MONTH_COLUMN,
    PERFORMANCE_1_YEAR_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/Performance";
import {
    PRIMARY_MARKET_ALLOWED_INVESTORS_COLUMN,
    PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/PrimaryMarket";
import { TOKENIZATION_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenizationType";
import { INCORPORATION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/IncorporationCountry";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "~/modules/assets/components/AssetTable/columns/RegulatoryFramework";
import {
    ASSET_CLASS_DESCRIPTIONS,
    ASSET_CLASS_PAGE_TO_SORT,
    ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS,
    ASSET_CLASS_PAGES_TO_FILTER,
} from "~/modules/asset_classes/consts";
import { SEARCH_FILTER_METADATA } from "~/modules/filters/consts";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

const SEO_TITLE = "Tokenized Credit";
const SEO_DESCRIPTION = ASSET_CLASS_DESCRIPTIONS.credit;

// // // //

const CREDIT_TOP_TRANSFERS_FILTERS: Filter[] = [
    {
        operator: FilterOperators.Or,
        filters: [
            {
                field: TransactionColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.CorporateCredit,
            },
            {
                field: TransactionColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.MunicipalBonds,
            },
            {
                field: TransactionColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.DiversifiedCredit,
            },
            {
                field: TransactionColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.AssetBackedCredit,
            },
            {
                field: TransactionColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.SpecialtyFinance,
            },
        ],
    },
];

const CREDIT_TABLE_COLUMNS: ColumnMetadata<AssetTableRowData, AssetColumns>[] =
    [
        ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE,
        MANAGER_COLUMN,
        BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
        NET_ASSET_VALUE_COLUMN,
        PERFORMANCE_1_MONTH_COLUMN,
        PERFORMANCE_1_YEAR_COLUMN,
        HOLDING_ADDRESS_COUNT_COLUMN,
        ASSET_CLASS_COLUMN_NO_LINK,
        TOKENIZATION_TYPE_COLUMN,
        PLATFORM_COLUMN,
        NETWORKS_COLUMN,
        PRIMARY_MARKET_ALLOWED_INVESTORS_COLUMN,
        INCORPORATION_COUNTRY_COLUMN,
        LICENSE_OR_REGULATORY_FRAMEWORK,
        PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
        MANAGEMENT_FEE_COLUMN,
        CONTRACT_ADDRESS_COLUMN,
    ];

const CREDIT_ASSETS_QUERY: ApiQuery = {
    filter: ASSET_CLASS_PAGES_TO_FILTER.credit,
    sort: ASSET_CLASS_PAGE_TO_SORT.credit,
    pagination: {
        page: 1,
        perPage: 25,
    },
};

function stripLinksFromLeagueTableTab(tab: LeagueTableTab): LeagueTableTab {
    if (tab.data.groupType !== LeagueTableGroupTypes.assetClasses) {
        return tab;
    }

    const updatedTab: LeagueTableTab = {
        ...tab,
        data: {
            ...tab.data,
            rows: tab.data.rows.map((row) => {
                return {
                    ...row,
                    group: {
                        ...row.group,
                        linkTo: null,
                    },
                };
            }),
        },
    };
    return updatedTab;
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
// NOTE - this will have to be changed to hit api.rwa.xyz when the API has been updated
export async function getStaticProps() {
    // Base filters for all credit queries
    const baseFilters: Filter[] =
        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.credit;

    // Make all requests in parallel + destructure responses
    const [
        listQueryResponse,
        filterMetadata,
        leagueTableTabs,
        netFlows,
        distributedAggregates,
        representedAggregates,
        holdersData,
        // Top Transfers
        transactions_7d_under100k,
        transactions_7d_under1m,
        transactions_7d_under10m,
        transactions_7d_under_100m,
        transactions_30d_under100k,
        transactions_30d_under1m,
        transactions_30d_under10m,
        transactions_30d_under_100m,
    ] = await Promise.all([
        queryAssets({
            query: {
                ...CREDIT_ASSETS_QUERY,
                pagination: {
                    page: 1,
                    perPage: 25,
                },
            },
        }),
        queryAssetFilterMeta({
            filter: ASSET_CLASS_PAGES_TO_FILTER.credit,
        }),
        queryLeagueTablesByDistributionType({
            name: "asset-class--credit",
            filter: {
                operator: FilterOperators.And,
                filters: baseFilters,
            },
            groups: [
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.managers,
                LeagueTableGroupTypes.networks,
                LeagueTableGroupTypes.assetClasses,
                LeagueTableGroupTypes.jurisdictionCountries,
            ],
        }),
        queryNetFlows({
            name: "asset-class--credit",
            filters: ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.credit,
            groupBy: TokenTimeseriesGroupBy.asset,
            limit: 8,
        }),
        // 1. Distributed Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--credit--distributed-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
            ],
        }),
        // 2. Represented Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--credit--represented-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
            ],
        }),
        // 3. Holders
        queryTokenAggregateValueWith30D({
            name: "asset-class--credit--total-holders",
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: baseFilters,
        }),
        // // // //
        // Top Transfers
        // 7-day transactions
        getTopTransfers({
            name: "asset-class--credit--7d-under-100k",
            filters: CREDIT_TOP_TRANSFERS_FILTERS,
            max: 100_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--credit--7d-under-1m",
            filters: CREDIT_TOP_TRANSFERS_FILTERS,
            max: 1_000_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--credit--7d-under-10m",
            filters: CREDIT_TOP_TRANSFERS_FILTERS,
            max: 10_000_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--credit--7d-under-100m",
            filters: CREDIT_TOP_TRANSFERS_FILTERS,
            timestamp: SEVEN_DAYS_AGO,
        }),
        // // // //
        // 30-day transactions
        getTopTransfers({
            name: "asset-class--credit--30d-under-100k",
            filters: CREDIT_TOP_TRANSFERS_FILTERS,
            max: 100_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--credit--30d-under-1m",
            filters: CREDIT_TOP_TRANSFERS_FILTERS,
            max: 1_000_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--credit--30d-under-10m",
            filters: CREDIT_TOP_TRANSFERS_FILTERS,
            max: 10_000_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--credit--30d-under-100m",
            filters: CREDIT_TOP_TRANSFERS_FILTERS,
            timestamp: THIRTY_DAYS_AGO,
        }),
    ]);

    const aggregates: Array<AggregateDisplayProps> = [
        {
            label: "Distributed Value",
            value: distributedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: distributedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Represented Value",
            value: representedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: representedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Assets",
            value: listQueryResponse.pagination.resultCount,
            type: AggregateValueTypes.count,
        },
        {
            label: "Holders",
            value: holdersData.value,
            type: AggregateValueTypes.count,
            percentChange: {
                value: holdersData.change30D,
                interval: "30d",
            },
        },
    ];

    // Trim down FilterMetadata options to only include the ones we need,
    // re-label some of the options for clarity + consistency
    const trimmedFilterMetadata = filterMetadata
        .filter((f) => {
            return [
                SEARCH_FILTER_METADATA.field,
                AssetColumns.protocolIDs,
                AssetColumns.networkIDs,
                AssetColumns.tokenizationTypeIDs,
                AssetColumns.assetClassID,
            ].includes(f.field as AssetColumns);
        })
        .map((f) => {
            if (f.field === AssetColumns.tokenizationTypeIDs) {
                // NOTE - we replace the defaultOperator with FilterOperators.Equals so it checks for exact equality of Asset.tokenization_type_ids (instead of just including the value)
                // This is mainly here to handle an edge-case where assets with multiple tokenization types are included when only one is selected
                const fm: FilterMetadata = {
                    ...f,
                    defaultOperator: FilterOperators.Equals,
                    content: {
                        ...f.content,
                        label: "Tokenization Type",
                    },
                };
                return fm;
            }

            return f;
        });

    // Modify the asset classes league table tab so the items are NOT linked to the asset class page
    const modifiedLeagueTableTabs: LeagueTableTabsByDistributionType = {
        all: leagueTableTabs.all.map((tab) =>
            stripLinksFromLeagueTableTab(tab),
        ),
        distributed: leagueTableTabs.distributed.map((tab) =>
            stripLinksFromLeagueTableTab(tab),
        ),
        represented: leagueTableTabs.represented.map((tab) =>
            stripLinksFromLeagueTableTab(tab),
        ),
    };

    return {
        props: {
            listQueryResponse,
            leagueTableTabs: modifiedLeagueTableTabs,
            netFlows,
            aggregates,
            filterMetadata: trimmedFilterMetadata,
            topTransfers: {
                seven_day: {
                    under_100k: transactions_7d_under100k,
                    under_1m: transactions_7d_under1m,
                    under_10m: transactions_7d_under10m,
                    under_100m: transactions_7d_under_100m,
                },
                thirty_day: {
                    under_100k: transactions_30d_under100k,
                    under_1m: transactions_30d_under1m,
                    under_10m: transactions_30d_under10m,
                    under_100m: transactions_30d_under_100m,
                },
            },
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

export default function CreditPage(props: {
    aggregates: Array<AggregateDisplayProps>;
    listQueryResponse: QueryResponse<Asset>;
    filterMetadata: FilterMetadata[];
    leagueTableTabs: LeagueTableTabsByDistributionType;
    netFlows: NetFlowsData;
    topTransfers: TopTransfersData;
}) {
    const {
        aggregates,
        listQueryResponse,
        filterMetadata,
        leagueTableTabs,
        netFlows,
        topTransfers,
    } = props;

    const modes = getTimeseriesModes({
        analyticsKey: "credit",
        defaultGroupBy: TokenTimeseriesGroupBy.asset,
        measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
        filters: ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.credit,
    });

    return (
        <PageTrack route={Routes.credit}>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.credit)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.credit),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "tokenized credit, private credit, DeFi lending, corporate credit, institutional lending, tokenized loans, credit protocols, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.credit}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.credit,
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => <Navbar route={Routes.credit} user={user} />}
            </UserFetcher>
            <SidebarLayout pathname={Routes.credit}>
                {/* <Breadcrumbs
                    pages={[
                        {
                            name: "Tokenized Treasuries",
                            current: true,
                        },
                    ]}
                /> */}
                <PageWrapper>
                    <AssetClassIndividualPage
                        listQueryResponse={listQueryResponse}
                        netFlows={{
                            title: "Tokenized Credit Net Flows",
                            data: netFlows,
                        }}
                        title={SEO_TITLE}
                        subtitle={SEO_DESCRIPTION}
                        assetsQuery={CREDIT_ASSETS_QUERY}
                        header={{
                            label: "Tokenized Credit Assets",
                            loading: false,
                            value: `${listQueryResponse.pagination.resultCount.toLocaleString()} Total`,
                            // description: "Expand rows to view different networks supported by each private equity product.",
                        }}
                        aggregates={aggregates}
                        timeseriesChart={{
                            title: "Tokenized Credit Metrics",
                            modes,
                            distributionSelect: {
                                distributed: {
                                    enabled: true,
                                },
                                represented: {
                                    enabled: true,
                                },
                                stablecoins: {
                                    enabled: false,
                                    active: false,
                                },
                            },
                            columnSpanConfig: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                                xxl: 12,
                            },
                        }}
                        pieChart={{
                            leagueTableData: leagueTableTabs,
                            title: "Tokenized Credit Value",
                            defaultGroupBy: LeagueTableGroupTypes.networks,
                            allowedGroupByValues: [
                                LeagueTableGroupTypes.networks,
                                LeagueTableGroupTypes.assetClasses,
                                LeagueTableGroupTypes.platforms,
                                LeagueTableGroupTypes.managers,
                                LeagueTableGroupTypes.jurisdictionCountries,
                            ],
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        leagueTable={{
                            leagueTableData: leagueTableTabs,
                            analyticsTableName: "tokenized-credit-league-table",
                            title: "Tokenized Credit League Table",
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        topTransfers={{
                            title: "Tokenized Credit Latest Transactions",
                            topTransfers: topTransfers,
                        }}
                        table={{
                            filterMetadata,
                            columns: CREDIT_TABLE_COLUMNS,
                            analyticsTableName: "tokenized-credit-assets",
                            exportFilename: "credit-assets",
                            sortQuery: {
                                field: AssetColumns.bridgedTokenValueDollar,
                                direction: SortDirections.desc,
                            },
                        }}
                    />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
